import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ErrorTextComponent,
  FormControlComponent,
  FormControlErrorDirective,
  HelpTextComponent,
  InputComponent,
  InputElementComponent,
  InputStatusIconComponent,
  InputWrapComponent,
  LabelComponent,
  SpinnerComponent,
} from '@mx51/chameleon-ui-angular';
import { IconModule } from '@mx51/shared-components';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { AutocompleteOption, ErrorMessages } from './autocomplete.interfaces';

@Component({
  selector: 'sd-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  standalone: true,
  imports: [
    FormControlComponent,
    LabelComponent,
    InputWrapComponent,
    InputElementComponent,
    SpinnerComponent,
    InputComponent,
    InputStatusIconComponent,
    HelpTextComponent,
    MatAutocompleteModule,
    ErrorTextComponent,
    MatTooltipModule,
    ReactiveFormsModule,
    NgClass,
    IconModule,
    NgIf,
    NgFor,
    FormControlErrorDirective,
  ],
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  @Input() autocompleteFormControl: FormControl<string | null>;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() required = false;
  @Input() options: AutocompleteOption[] = [];
  @Input() loading = false;
  @Input() showError = false;
  @Input() overrideErrorMessages: Partial<ErrorMessages> = {};
  @Input() useAsyncRequest = false;
  @Input() minCharactersForAsyncRequest = 3;
  @Input() helpText: string;
  @Output() asyncRequestStarted = new EventEmitter<string>();
  @Output() optionSelected = new EventEmitter();
  @Output() blurEvent = new EventEmitter<void>();
  @Output() focusEvent = new EventEmitter<void>();

  defaultErrorMessages: ErrorMessages = {
    autocompleteNoResult: 'No result',
  };
  errorMessages: ErrorMessages;

  private inputValueChanges = new Subject<string>();
  private onDestroy = new Subject<void>();

  ngOnInit() {
    this.setupErrorMessages();
    this.setupAsyncRequest();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  selectOption() {
    this.optionSelected.emit(this.autocompleteFormControl.value);
  }

  handleValueChange(event: KeyboardEvent) {
    this.inputValueChanges.next((event.target as HTMLInputElement).value);
  }

  onBlur(): void {
    this.blurEvent.emit();
  }

  onFocus(): void {
    this.focusEvent.emit();
  }

  private setupAsyncRequest() {
    if (this.useAsyncRequest) {
      this.inputValueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          filter((value: string) => !!value),
          map((value: string) => value.trim()),
          filter((value: string) => value.length >= this.minCharactersForAsyncRequest),
          takeUntil(this.onDestroy),
        )
        .subscribe((value: string) => {
          this.asyncRequestStarted.emit(value);
        });
    }
  }

  private setupErrorMessages() {
    this.errorMessages = { ...this.defaultErrorMessages, ...this.overrideErrorMessages };
  }
}
